/**
 * Orchestra AI Platform - Template Library Component
 * 
 * Component for browsing and using project templates
 * 
 * @fileoverview Template library with categories and preview
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import './Workspace.css';

/**
 * Template Library Component
 * @param {Object} props
 * @param {Object} [props.currentUser] - Current user object
 * @param {Function} [props.onTemplateSelect] - Handler for template selection
 */
export function TemplateLibrary({ currentUser, onTemplateSelect }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadTemplates();
  }, []);

  /**
   * Load templates from database
   */
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('developer_templates')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      toast.error('Failed to load templates: ' + error.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply template to create project
   * @param {Object} template
   */
  const applyTemplate = async (template) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create project from template
      const { data: project, error } = await supabase
        .from('developer_projects')
        .insert({
          user_id: user.id,
          name: `${template.name} (Copy)`,
          description: template.description || '',
          type: template.project_type || 'workflow',
          status: 'active',
          settings: template.settings || {
            auto_save: true,
            collaboration_enabled: false,
            default_timeout: 30000,
          },
        })
        .select()
        .single();

      if (error) throw error;

      // Copy workflows from template if available
      if (template.workflow_data) {
        const { error: workflowError } = await supabase.from('developer_workflows').insert({
          project_id: project.id,
          user_id: user.id,
          name: 'Main Workflow',
          description: '',
          workflow_data: template.workflow_data,
        });

        if (workflowError) {
          console.error('Failed to create workflow from template:', workflowError);
        }
      }

      toast.success('Project created from template');
      if (onTemplateSelect) {
        onTemplateSelect(project.id);
      }
    } catch (error) {
      toast.error('Failed to use template: ' + error.message);
    }
  };

  if (loading) {
    return <div className="template-library-loading">Loading templates...</div>;
  }

  const categories = ['all', ...new Set(templates.map((t) => t.category).filter(Boolean))];
  const filteredTemplates =
    selectedCategory === 'all'
      ? templates
      : templates.filter((t) => t.category === selectedCategory);

  return (
    <div className="template-library">
      <div className="template-library-header">
        <h2>Template Library</h2>
        <div className="template-categories">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`template-category-button ${selectedCategory === category ? 'active' : ''}`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {filteredTemplates.length === 0 ? (
        <div className="template-library-empty">
          <p>No templates found</p>
          <p className="hint">Templates will appear here when available</p>
        </div>
      ) : (
        <div className="template-library-grid">
          {filteredTemplates.map((template) => (
            <div key={template.id} className="template-card">
              <h3 className="template-card-title">{template.name}</h3>
              {template.description && (
                <p className="template-card-description">{template.description}</p>
              )}
              {template.category && (
                <span className="template-card-category">
                  {template.category}
                </span>
              )}
              <button
                onClick={() => applyTemplate(template)}
                className="template-card-button"
              >
                Use Template
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

TemplateLibrary.propTypes = {
  currentUser: PropTypes.object,
  onTemplateSelect: PropTypes.func,
};

